import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const STEPS = [
  { id: 1, label: 'Initializing AI model' },
  { id: 2, label: 'Configuring instructions' },
  { id: 3, label: 'Indexing knowledge base' },
  { id: 4, label: 'Optimizing responses' },
  { id: 5, label: 'Activating voice engine' },
  { id: 6, label: 'Finalizing deployment' },
  { id: 7, label: 'Agent Created' },
];

const TIPS = [
  'You can edit your prompt anytime.',
  'Upload documents later to improve responses.',
  'Connect WhatsApp, Voice, or your website after setup.',
  'Test your assistant before publishing.',
  'Add custom variables to personalize conversations.',
  'Use the Knowledge Base to improve accuracy.',
];

const STEP_DURATIONS = [2200, 2500, 2800, 2400, 2600, 2300, 1800];

const AgentLoadingScreen = ({ isOpen, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [tipIndex, setTipIndex] = useState(0);
  const [tipVisible, setTipVisible] = useState(true);
  const [allDone, setAllDone] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(0);
      setCompletedSteps([]);
      setAllDone(false);
      setTipIndex(0);
      return;
    }

    let cancelled = false;
    const timeoutIds = [];

    const runSteps = async () => {
      for (let i = 0; i < STEPS.length; i++) {
        if (cancelled) return;
        setCurrentStep(i);
        await new Promise((resolve) => {
          const id = setTimeout(resolve, STEP_DURATIONS[i] ?? 2000);
          timeoutIds.push(id);
        });
        if (cancelled) return;
        setCompletedSteps((prev) => [...prev, i]);
      }
      if (!cancelled) {
        setAllDone(true);
        const id = setTimeout(() => { if (!cancelled) onComplete?.(); }, 900);
        timeoutIds.push(id);
      }
    };

    runSteps();

    return () => {
      cancelled = true;
      timeoutIds.forEach(clearTimeout);
    };
  }, [isOpen]);

  // Rotate tips
  useEffect(() => {
    if (!isOpen) return;
    const interval = setInterval(() => {
      setTipVisible(false);
      setTimeout(() => {
        setTipIndex((prev) => (prev + 1) % TIPS?.length);
        setTipVisible(true);
      }, 400);
    }, 4000);
    return () => clearInterval(interval);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-white/95 dark:bg-gray-950/95 backdrop-blur-sm">
      {/* Subtle background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-violet-50/60 via-white to-indigo-50/40 pointer-events-none" />
      <div className="relative w-full max-w-md mx-4">
        {/* Main card */}
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100/80 overflow-hidden">
          {/* Top accent bar */}
          <div className="h-1 w-full bg-gradient-to-r from-violet-500 via-indigo-500 to-violet-600" />

          <div className="px-8 pt-8 pb-6">
            {/* Icon + Title */}
            <div className="flex flex-col items-center mb-8">
              <div className="relative mb-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-200">
                  <Icon name="Bot" size={26} className="text-white" />
                </div>
                {!allDone && (
                  <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white border-2 border-violet-200 flex items-center justify-center">
                    <span className="w-2.5 h-2.5 rounded-full bg-violet-500 animate-pulse" />
                  </span>
                )}
                {allDone && (
                  <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-green-500 border-2 border-white flex items-center justify-center">
                    <Icon name="Check" size={10} className="text-white" />
                  </span>
                )}
              </div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">
                Preparing your AI Agent
              </h1>
              <p className="text-sm text-gray-400 mt-1">
                {allDone ? 'Your assistant is ready!' : 'This will only take a moment…'}
              </p>
            </div>

            {/* Steps checklist */}
            <div className="space-y-3">
              {STEPS?.map((step, idx) => {
                const isCompleted = completedSteps?.includes(idx);
                const isCurrent = currentStep === idx && !isCompleted;
                const isPending = !isCompleted && !isCurrent;

                return (
                  <div
                    key={step?.id}
                    className="flex items-center gap-3.5"
                    style={{
                      opacity: isPending ? 0.45 : 1,
                      transition: 'opacity 0.4s ease',
                    }}
                  >
                    {/* Step indicator */}
                    <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                      {isCompleted ? (
                        <div
                          className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center"
                          style={{
                            animation: 'checkIn 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
                          }}
                        >
                          <Icon name="Check" size={12} className="text-white" />
                        </div>
                      ) : isCurrent ? (
                        <div className="w-6 h-6 rounded-full border-2 border-violet-500 border-t-transparent animate-spin" />
                      ) : (
                        <div className="w-6 h-6 rounded-full border-2 border-gray-200" />
                      )}
                    </div>
                    {/* Label */}
                    <span
                      className={`text-sm font-medium transition-colors duration-300 ${
                        isCompleted
                          ? 'text-green-600'
                          : isCurrent
                          ? 'text-violet-700'
                          : 'text-gray-400'
                      }`}
                    >
                      {step?.label}
                    </span>
                    {/* Current step pulse dot */}
                    {isCurrent && (
                      <span className="ml-auto flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Tip section */}
          <div className="px-8 pb-7">
            <div className="mt-4 px-4 py-3 bg-gray-50 rounded-xl border border-gray-100 min-h-[52px] flex items-center gap-3">
              <div className="w-6 h-6 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Icon name="Lightbulb" size={13} className="text-amber-500" />
              </div>
              <p
                className="text-xs text-gray-500 leading-relaxed"
                style={{
                  opacity: tipVisible ? 1 : 0,
                  transform: tipVisible ? 'translateY(0)' : 'translateY(4px)',
                  transition: 'opacity 0.4s ease, transform 0.4s ease',
                }}
              >
                <span className="font-semibold text-gray-600">Tip: </span>
                {TIPS?.[tipIndex]}
              </p>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes checkIn {
          0% { transform: scale(0.4); opacity: 0; }
          60% { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default AgentLoadingScreen;
